"use client";

/**
 * Boutons « voir cette page dans une autre langue » (FR ⇄ EN ⇄ AR).
 *
 * Auto-suffisant : depuis le chemin courant, il rend UN bouton par autre locale
 * qui possède un vrai jumeau pour cette page (localeHasPage → jamais de 404),
 * avec l'URL cible dérivée (localeAlternatePath, source de vérité des slugs).
 *
 * IMPORTANT : au clic, on MÉMORISE le choix (setPreferredLocale) — sinon le
 * composant LocalePreference, voyant une préférence stockée différente,
 * rebasculerait immédiatement l'utilisateur (le bouton « ne marchait pas »).
 * Le libellé est écrit DANS la langue cible (repère visuel universel).
 */
import Link from "next/link";
import { usePathname } from "next/navigation";
import { Icon } from "@/components/ui/Icon";
import { LOCALES, type Locale, localeFromPath, HREFLANG } from "@/config/i18n";
import { localeAlternatePath, localeHasPage } from "@/config/localeRoutes";
import { setPreferredLocale } from "@/components/i18n/preferredLocale";

/** « Voir dans la langue X », phrasé dans la langue cible. */
const VIEW_IN_LABEL: Record<Locale, string> = {
  fr: "Voir en français",
  en: "View in English",
  ar: "عرض بالعربية",
};

export function LocaleSwitchLink(): JSX.Element | null {
  const pathname = usePathname() ?? "/";
  const current = localeFromPath(pathname);
  const targets = LOCALES.filter(
    (locale) => locale !== current && localeHasPage(pathname, locale),
  );
  if (targets.length === 0) return null;

  return (
    <div className="inline-flex flex-wrap items-center gap-2">
      {targets.map((target) => (
        <Link
          key={target}
          href={localeAlternatePath(pathname, target)}
          hrefLang={HREFLANG[target]}
          onClick={() => setPreferredLocale(target)}
          className="inline-flex items-center gap-1.5 rounded-full border border-black/15 px-3 py-1 text-xs font-medium text-black/70 transition-colors hover:border-primary hover:text-black"
        >
          <Icon icon="mdi:translate" className="h-3.5 w-3.5" />
          {VIEW_IN_LABEL[target]}
        </Link>
      ))}
    </div>
  );
}

export default LocaleSwitchLink;
